import type { JsonObject, JsonValue } from "./json";

function object(value: JsonValue | undefined): JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};
}

/** Upgrade old DB snapshots at the read boundary; authored fields carry their own annotations. */
export function normalizeModelParameterSchema(source: Readonly<JsonObject>): JsonObject {
  const result: JsonObject = structuredClone(source);
  const input = object(result.input);
  const properties = object(input.properties);
  const ui = object(result.ui);
  const fields = object(ui.fields);
  const defaults = object(result.defaults);
  const order = Array.isArray(ui.order) ? ui.order : [];
  for (const [key, value] of Object.entries(properties)) {
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    const position = order.indexOf(key);
    const decoration = {
      ...(position >= 0 ? { order: position } : {}),
      ...object(fields[key]),
      ...object(value["x-ui"]),
    };
    if (Object.keys(decoration).length) value["x-ui"] = decoration;
    if (key in defaults) value.default = defaults[key]!;
  }
  delete result.ui;
  delete result.defaults;
  return result;
}

export function modelInputDefaults(input: JsonObject): JsonObject {
  return Object.fromEntries(Object.entries(object(input.properties)).flatMap(([key, value]) => {
    const field = object(value);
    return "default" in field ? [[key, structuredClone(field.default!)]] : [];
  }));
}

export function orderedSchemaProperties(schema: JsonObject): [string, JsonValue][] {
  const rank = (value: JsonValue) => {
    const order = object(object(value)["x-ui"]).order;
    return typeof order === "number" ? order : Number.MAX_SAFE_INTEGER;
  };
  return Object.entries(object(schema.properties)).sort(([, a], [, b]) => rank(a) - rank(b));
}

/** Compatibility projection for existing schema API clients, never a second authoring source. */
export function modelInputUi(input: JsonObject): JsonObject {
  const entries = orderedSchemaProperties(input);
  return {
    order: entries.map(([key]) => key),
    fields: Object.fromEntries(entries.flatMap(([key, value]) => {
      const { order: _order, ...ui } = object(object(value)["x-ui"]);
      return Object.keys(ui).length ? [[key, ui]] : [];
    })),
  };
}
